/**
 * Pages with unsaved changes, for the one navigation the router cannot block:
 * an account switch (ADR-0386).
 *
 * `switchAccount` POSTs first and navigates second (ADR-0302, Decision 5). A
 * router blocker on that navigation would leave the URL on the old account
 * with the cookie on the new one, so the switch asks BEFORE the POST, here.
 * `useUnsavedChangesGuard` registers itself while its page is dirty.
 *
 * Plain module state, no React and no router: `AuthContext` imports it, and
 * it must import nothing that imports `AuthContext` back.
 */

/** Asks the customer; resolves `true` to discard the changes, `false` to stay. */
export type UnsavedChangesAsk = () => Promise<boolean>;

const guards: UnsavedChangesAsk[] = [];

/** Registers a dirty page's question; returns the unregister function. */
export function registerUnsavedGuard(ask: UnsavedChangesAsk): () => void {
  guards.push(ask);
  return () => {
    const i = guards.lastIndexOf(ask);
    if (i >= 0) guards.splice(i, 1);
  };
}

/**
 * `true` when nothing is unsaved or the customer chose to discard it; `false`
 * when they chose to stay. Asks the most recently registered page, the same
 * one the router's blocker consults.
 */
export function confirmDiscardUnsaved(): Promise<boolean> {
  const ask = guards[guards.length - 1];
  return ask ? ask() : Promise.resolve(true);
}
